import { useNotifications } from '../../../_lib/notifications/useNotifications';
import {
  SubmissionStatus,
  type SubmissionDetailsDto,
  type SubmissionTableRow,
} from '../../../api/submissions/submissions.api.types';
import { useAuth } from '../../../components/hooks/useAuth';
import { useIsCarrierSubmissionsEnabled } from '../../../components/hooks/useIsCarrierSubmissionsEnabled';
import { usePersona } from '../../../components/hooks/usePersona';
import type { Nullable } from '../../../types/common/utility';
import { PERSONA_TYPES } from '../../_statics/persona.statics';
import {
  RequestQuoteSteps,
  UNKNOWN_PRODUCT,
} from '../../agencies/quotes/PrimeX/constants';
import { SUBMISSION_SOURCE } from '../cowbell/cowbellSubmissionsListing/useColumnConfig';
import { submissionStatusLabel } from './submissionStatusLabel';

export const useCanPerformActionOnSubmission = (
  submission: Nullable<SubmissionDetailsDto | SubmissionTableRow>
) => {
  const { isCowbellUserJumped } = useAuth();
  const persona = usePersona();
  const isCarrierSubmissionsEnabled = useIsCarrierSubmissionsEnabled();

  if (persona.isCowbell || isCowbellUserJumped) {
    return true;
  }
  if (persona.isCarrier) {
    return isCarrierSubmissionsEnabled;
  }
  if (!persona.isAgency) {
    return false;
  }
  // Agency personas may act on their own submissions, but never on Cowbell
  // ones or on extractor-sourced ones — both EMAIL and OMNI (chat) are
  // extractor-sourced.
  if (submission?.createdByAccountType === PERSONA_TYPES.COWBELL) {
    return false;
  }
  if (submission?.submissionSource === SUBMISSION_SOURCE.EMAIL) {
    return false;
  }
  if (submission?.submissionSource === SUBMISSION_SOURCE.OMNI) {
    return false;
  }
  return true;
};

interface SubmissionQuoteLinkPersona {
  readonly basePath?: string;
}

/**
 * Pure builder for a submission's request-quote link. Kept pure so callers
 * holding a submission id at callback time (not render time) can reuse the
 * exact same routing.
 */
export const buildSubmissionRequestQuoteLink = (
  persona: SubmissionQuoteLinkPersona,
  id: Nullable<string>,
  step: RequestQuoteSteps = RequestQuoteSteps.VERIFY_SUBMISSION_INFO,
  product: string = UNKNOWN_PRODUCT
) => {
  return `${persona.basePath}/submissions/quotes/${product}/${id}/requestQuote/${step}`;
};

/**
 * Details deep-link for a submission. Renewals must use the renewal shell
 * (`/submissions/renewal/...`); NB uses `/submissions/summary/...`.
 */
export const buildSubmissionDetailsLink = (
  basePath: string,
  submissionId: string,
  isRenewal: boolean,
  tab = 'summary'
) => {
  const view = isRenewal ? 'renewal' : 'summary';
  return `${basePath}/submissions/${view}/${submissionId}/${tab}`;
};

/** Returns the request quote link for a submission, built from the step and product. */
export const useGetSubmissionRequestQuoteLink = (
  id: Nullable<string>,
  step: RequestQuoteSteps = RequestQuoteSteps.VERIFY_SUBMISSION_INFO,
  product: string = UNKNOWN_PRODUCT
) => {
  const persona = usePersona();

  return buildSubmissionRequestQuoteLink(persona, id, step, product);
};

export const buildSubmissionNextRequestQuoteLink = (
  basePath: string,
  id: Nullable<string>,
  productKey: string = UNKNOWN_PRODUCT,
  customize = false
) => {
  const baseUrl = `${basePath}/submissions/quote/${id}/${productKey}`;
  return customize ? `${baseUrl}/customize` : baseUrl;
};

export const useGetSubmissionNextRequestQuoteLink = (
  id: Nullable<string>,
  productKey: string = UNKNOWN_PRODUCT,
  customize = false
) => {
  const persona = usePersona();

  return buildSubmissionNextRequestQuoteLink(
    persona.basePath,
    id,
    productKey,
    customize
  );
};

/**
 * Maps submission status for display purposes.
 */
export const getDisplayStatus = (status: string | undefined): string =>
  submissionStatusLabel(status);

export const shouldShowTriagingOutcome = (submission: SubmissionDetailsDto) => {
  const statusesToShow = [
    SubmissionStatus.REFERRAL,
    SubmissionStatus.NON_RENEWED,
    SubmissionStatus.SCHEDULED,
  ];
  return (
    submission.isRenewal &&
    submission.triagingOutcome != null &&
    statusesToShow.includes(submission.triagingOutcome)
  );
};

export const showTriageOutcomeNotification = (
  submission: SubmissionDetailsDto,
  notifications: ReturnType<typeof useNotifications>
) => {
  if (shouldShowTriagingOutcome(submission)) {
    const message = `Submission has been triaged as ${submission.triagingOutcome
      .toLowerCase()
      .split('_')
      .join(' ')}.`;

    notifications.info(message, <>Please check submission notes for details</>);
  }
};
